import { useNavigate } from 'react-router-dom';
import { Sparkles, Camera } from 'lucide-react';

/**
 * Q39 Phase B — MemoriesStrip
 *
 * Auto-pulls cover images from plans the user has hosted or attended
 * (`compat.memories` populated by backend `compute_activity`). Renders a
 * horizontally-scrolling strip of tiles so the profile feels alive — every
 * tile links to the original plan so visitors can see what kind of moments
 * this person actually shows up to.
 *
 * Renders nothing when `memories` is empty.
 */
const CATEGORY_EMOJI = {
  fitness: '🏃',
  wellness: '🧘',
  coffee: '☕',
  food: '🍣',
  outdoors: '🥾',
  travel: '✈️',
  culture: '🎨',
  art: '🎨',
  social: '🤝',
  hosting: '🎉',
  nightlife: '🌙',
  sports: '🏅',
  music: '🎵',
  business: '💼',
  networking: '🤝',
  wellbeing: '🧘',
  brunch: '🥐',
  beach: '🏖️',
};

function emojiFor(category) {
  if (!category) return '✨';
  return CATEGORY_EMOJI[(category || '').toLowerCase()] || '✨';
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch { return ''; }
}

export default function MemoriesStrip({ memories, isOwn, displayName }) {
  const navigate = useNavigate();
  if (!Array.isArray(memories) || memories.length === 0) return null;

  return (
    <section
      data-testid="profile-memories"
      className="bg-white rounded-2xl border border-black/[0.06] p-4 mt-3"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#3F5200]" strokeWidth={2.6} />
          <h3 className="text-sm font-extrabold text-[#111]">Recent memories</h3>
        </div>
        <span className="text-[10px] font-bold text-[#9B9EA3] uppercase tracking-wider">
          {memories.length} {memories.length === 1 ? 'moment' : 'moments'}
        </span>
      </div>

      <div
        data-testid="memories-strip"
        className="flex gap-2.5 overflow-x-auto -mx-4 px-4 pb-1"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {memories.map((m) => (
          <MemoryTile key={m.id} memory={m} onTap={() => navigate(`/plan/${m.id}`)} />
        ))}
      </div>

      {isOwn && (
        <p className="text-[11px] text-[#7A7E83] mt-3 leading-relaxed">
          Memories auto-update when you host or attend plans.
        </p>
      )}
    </section>
  );
}

function MemoryTile({ memory, onTap }) {
  const { title, category, cover_image, date, is_host, id } = memory || {};
  const emoji = emojiFor(category);
  const dateLabel = formatDate(date);

  return (
    <button
      type="button"
      onClick={onTap}
      data-testid={`memory-tile-${id}`}
      className="relative shrink-0 w-[136px] h-[170px] rounded-2xl overflow-hidden bg-[#1A1A1A] text-left focus:outline-none focus:ring-2 focus:ring-[#B6E600] transition-transform active:scale-[0.98]"
      style={{ touchAction: 'manipulation' }}
    >
      {cover_image ? (
        <img
          src={cover_image}
          alt={title || 'memory'}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#2A2A2A] to-[#0F0F0F]">
          <Camera size={28} className="text-white/40" strokeWidth={1.8} />
        </div>
      )}
      {/* Bottom gradient for legibility */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

      {/* Host badge */}
      {is_host && (
        <span
          data-testid={`memory-host-badge-${id}`}
          className="absolute top-2 left-2 text-[9px] font-extrabold uppercase tracking-wider bg-[#B6E600] text-[#111] px-1.5 py-0.5 rounded-full"
        >
          Hosted
        </span>
      )}

      {/* Category emoji top-right */}
      <span
        className="absolute top-2 right-2 text-[16px] leading-none bg-black/40 backdrop-blur-sm rounded-full w-7 h-7 flex items-center justify-center"
        aria-label={category || 'category'}
      >
        {emoji}
      </span>

      {/* Title + date */}
      <div className="absolute inset-x-0 bottom-0 p-2.5">
        <p className="text-[12.5px] font-extrabold text-white leading-tight line-clamp-2">
          {title || 'Untitled plan'}
        </p>
        {dateLabel && (
          <p className="text-[10px] text-white/75 font-bold mt-0.5">{dateLabel}</p>
        )}
      </div>
    </button>
  );
}
